'use client'

import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import React from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { navigateToPDP, getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'

import type { InfiniteScrollProductListProps } from './infinite-scroll-product-list-ds.types'

/* eslint-disable i18next/no-literal-string -- Panda CSS layout/spacing values (not user-facing copy) */
const wrapperClass = css({ display: 'grid', width: 'full' })
// Legacy-faithful DEFAULT grid gap (infiniteScrollProductList.module.scss: 32px desktop, 1.9rem mobile).
// Applied ONLY when a consumer does not supply its own grid spacing via `swiperClassName`. Layering this
// under a consumer override would make its `_mobileLegacy` conditional gap win the atomic-CSS cascade,
// which is why consumers previously needed `!important`; keeping it off when overridden removes that need.
const wrapperMobileOverride = { _mobileLegacy: { gap: '1.9rem' } }
const defaultGapClass = css({ gap: 'space-32' }, wrapperMobileOverride)
/* eslint-enable i18next/no-literal-string */

// eslint-disable-next-line i18next/no-literal-string -- Panda CSS layout values (not user-facing copy)
const productCardClass = css({
  width: 'initial',
  pr: 0,
})

export default function InfiniteScrollProductListDS({
  productCards,
  swiperClassName,
  gridColCount,
  tracker,
  productCardDesktopClassName,
  productCardExpClassName,
  productCardMobileClassName,
  wishlistIconOverrideClass,
  maxWidth,
  hideReviews,
  hideOptions,
  hideWishlistIcon,
  productClickTrackConfig,
  optionLimit,
  hideProductName,
  productCardLinkClass,
  hideTags,
  skipTagRefresh,
  productViewTracker,
  showWhiteGloveDeliveryTag,
  eagerLoadImageCount = 0,
  productTestIdPrefix,
}: InfiniteScrollProductListProps) {
  const router = useRouter()
  return (
    <div
      className={cx(wrapperClass, swiperClassName ? undefined : defaultGapClass, swiperClassName)}
      style={gridColCount ? { gridTemplateColumns: `repeat(${gridColCount}, 1fr)` } : undefined}
    >
      {productCards.map((productCard, idx) => {
        const initialSelectedVariant = getInitialSelectedVariant({
          cardVariants: productCard.product.cardVariants,
        })
        const _tracker = tracker ? `${tracker}__0__${idx}` : undefined
        return (
          <ProductCard
            key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
            data-testid={productTestIdPrefix ? TEST_LOCATORS.SECTION_PRODUCT(productTestIdPrefix, idx + 1) : undefined}
            className={cx(
              productCardClass,
              productCardDesktopClassName,
              productCardExpClassName,
              productCardMobileClassName,
            )}
            wishlistIconClassName={cx(wishlistIconOverrideClass)}
            productCard={productCard}
            maxWidth={maxWidth}
            hideReviews={hideReviews}
            hideOptions={hideOptions}
            hideProductName={hideProductName}
            tracker={_tracker}
            skipTagRefresh={skipTagRefresh}
            productClickTrackConfig={
              productClickTrackConfig
                ? {
                    ...productClickTrackConfig,
                    value: productCard.product.slug,
                    context: {
                      ...productClickTrackConfig.context,
                      row: 0,
                      column: idx,
                      productPosition: idx,
                      product_position: idx,
                      product_color: initialSelectedVariant?.displayConfig.value,
                      contentContainerType:
                        productClickTrackConfig.context?.contentContainerType ||
                        CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
                      contentContainerName:
                        productClickTrackConfig.context?.contentContainerName ||
                        CUSTOM_SECTION_CONTAINER_NAMES.INFINITE_LIST,
                      contentContainerDisplayName:
                        // eslint-disable-next-line i18next/no-literal-string -- telemetry fallback value, not user-facing copy
                        productClickTrackConfig.context?.contentContainerDisplayName || 'Infinite Scroll Product List',
                    },
                  }
                : undefined
            }
            shouldShowMore
            optionLimit={optionLimit}
            onShowMoreClick={({ selectedVariant }) => {
              navigateToPDP({
                slug: productCard.product.slug,
                tracker: _tracker,
                selectedOption: selectedVariant?.displayConfig.value,
                router,
              })
            }}
            hideWishlistIcon={hideWishlistIcon}
            productCardLinkClass={productCardLinkClass}
            hideTags={hideTags}
            productViewTracker={productViewTracker}
            showWhiteGloveDeliveryTag={showWhiteGloveDeliveryTag}
            eagerLoadImage={idx < eagerLoadImageCount}
          />
        )
      })}
    </div>
  )
}
